import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { MonoText } from "@/components/ui/CopyButton";
import { LoadingState, ErrorState } from "@/components/ui/States";
import { formatIDR } from "@/lib/format";

/**
 * Dashboard owner.
 *
 * Prinsip tata letaknya, karena halaman ini dibaca orang yang tidak mengurusi
 * desain: satu angka paling penting dibuat paling besar (pendapatan), angka
 * pendukung dikelompokkan di bawahnya, lalu tindakan yang bisa diambil.
 * Label memakai bahasa sehari-hari, bukan nama kolom database.
 */

interface Stats {
  total_products: number;
  total_variants: number;
  total_users: number;
  total_orders: number;
  total_paid: number;
  total_revenue: number;
  archived_products: number;
}

type Earnings = Awaited<ReturnType<typeof api.getEarnings>>;

const ACTIONS = [
  {
    to: "/admin/products",
    title: "Produk",
    desc: "Tambah produk baru, ubah harga, atau sembunyikan yang sudah tidak dijual.",
  },
  {
    to: "/admin/inventory",
    title: "Stok Akun",
    desc: "Masukkan daftar akun yang akan dikirim otomatis ke pembeli.",
  },
  {
    to: "/admin/orders",
    title: "Transaksi",
    desc: "Lihat semua pesanan yang masuk beserta status pembayarannya.",
  },
  {
    to: "/admin/settings",
    title: "Tampilan Situs",
    desc: "Ubah teks, gambar, dan tombol di halaman depan toko Anda.",
  },
];

export function AdminOverviewPage() {
  const { user, loading: authLoading } = useAuth();
  const [stats, setStats] = useState<Stats | null>(null);
  const [earnings, setEarnings] = useState<Earnings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!authLoading && user?.role === "owner") {
      api.getAdminStats()
        .then(setStats)
        .catch((e) => setError(e.message))
        .finally(() => setLoading(false));
      // Terpisah dari stats: Fansku bisa lambat/bermasalah, dan dashboard
      // tetap harus tampil walau angka gateway tidak tersedia.
      api.getEarnings().then(setEarnings).catch(() => setEarnings(null));
    } else if (!authLoading) {
      setLoading(false);
    }
  }, [user, authLoading]);

  if (authLoading) return <LoadingState label="MEMERIKSA_SESI" />;
  if (!user) return <NotAuthorized needs="masuk terlebih dahulu" />;
  if (user.role !== "owner") return <NotAuthorized needs="akses owner" />;

  const unpaid = stats ? Math.max(0, stats.total_orders - stats.total_paid) : 0;
  const conversion =
    stats && stats.total_orders > 0
      ? Math.round((stats.total_paid / stats.total_orders) * 100)
      : null;

  return (
    <div className="max-w-6xl mx-auto px-4 py-10">
      <header className="mb-10">
        <h1 className="font-heading text-3xl font-bold tracking-tight text-ink-primary">
          Dashboard
        </h1>
        <p className="mt-2 font-sans text-sm text-ink-muted">
          Ringkasan toko Anda. Masuk sebagai{" "}
          <span className="text-ink-primary">{user.email}</span>.
        </p>
      </header>

      {loading ? (
        <LoadingState label="MEMUAT_DATA" />
      ) : error ? (
        <ErrorState error={error} onRetry={() => window.location.reload()} />
      ) : !stats ? (
        <LoadingState />
      ) : (
        <>
          {/* Angka utama — pendapatan dibuat dominan. */}
          <section className="grid gap-4 lg:grid-cols-3 mb-4">
            <div className="lg:col-span-2 border border-border-strong bg-surface rounded-lg p-6">
              <p className="font-sans text-sm text-ink-muted">
                Pendapatan bersih <span className="text-ink-dim">(menurut Fansku)</span>
              </p>
              <p className="mt-2 font-mono text-4xl md:text-5xl tabular-nums text-accent">
                {earnings?.available
                  ? formatIDR(earnings.total_creator_earning)
                  : formatIDR(stats.total_revenue)}
              </p>

              {earnings?.available ? (
                <>
                  <p className="mt-3 font-sans text-sm text-ink-muted">
                    Dari {earnings.total_supports} pembayaran · pembeli membayar{" "}
                    {formatIDR(earnings.total_amount)}, selisihnya biaya gateway
                  </p>
                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <div className="border border-border bg-panel rounded-sm px-3 py-2">
                      <p className="font-sans text-xs text-ink-muted">Saldo bisa ditarik</p>
                      <p className="font-mono text-lg text-ink-primary">
                        {formatIDR(earnings.available_balance)}
                      </p>
                    </div>
                    <div className="border border-border bg-panel rounded-sm px-3 py-2">
                      <p className="font-sans text-xs text-ink-muted">Belum cair</p>
                      <p className="font-mono text-lg text-ink-primary">
                        {formatIDR(earnings.pending_balance)}
                      </p>
                    </div>
                  </div>
                  {/* Selisih wajar: yang dicatat toko adalah harga produk,
                      yang dicatat Fansku adalah uang yang benar-benar masuk. */}
                  <p className="mt-3 font-sans text-xs text-ink-dim">
                    Tercatat di toko: {formatIDR(stats.total_revenue)} dari{" "}
                    {stats.total_paid} pesanan
                    {conversion !== null && <> · {conversion}% dari pesanan masuk</>}
                  </p>
                </>
              ) : (
                <>
                  <p className="mt-3 font-sans text-sm text-ink-muted">
                    Dari {stats.total_paid} pesanan yang sudah dibayar
                    {conversion !== null && <> · {conversion}% dari pesanan masuk</>}
                  </p>
                  <p className="mt-2 font-sans text-xs text-status-pending">
                    Angka ini dari catatan toko, bukan Fansku
                    {earnings && !earnings.available && <> — {earnings.error}</>}
                  </p>
                </>
              )}
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-1 gap-4">
              <Metric label="Pesanan dibayar" value={stats.total_paid} />
              <Metric
                label="Belum dibayar"
                value={unpaid}
                tone={unpaid > 0 ? "warn" : "normal"}
              />
            </div>
          </section>

          {/* Angka pendukung. */}
          <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <Metric label="Produk" value={stats.total_products} />
            <Metric label="Paket / varian" value={stats.total_variants} />
            <Metric label="Pelanggan" value={stats.total_users} />
            <Metric label="Total pesanan" value={stats.total_orders} />
          </section>

          {/* Saran konkret — hanya muncul kalau memang relevan. */}
          {stats.archived_products > 0 && (
            <Notice
              text={`${stats.archived_products} produk sedang diarsip dan tidak terlihat di katalog. Buka arsip untuk memulihkannya.`}
              to="/admin/products"
              cta="Lihat arsip"
            />
          )}
          {stats.total_products === 0 && (
            <Notice
              text="Belum ada produk aktif. Tambahkan produk baru, atau pulihkan yang sudah diarsip, agar toko bisa menerima pesanan."
              to="/admin/products"
              cta="Kelola produk"
            />
          )}
          {stats.total_products > 0 && stats.total_variants === 0 && (
            <Notice
              text="Produk Anda belum punya paket harga, jadi belum bisa dibeli. Tambahkan minimal satu paket."
              to="/admin/products"
              cta="Atur paket"
            />
          )}
          {unpaid > 0 && (
            <Notice
              text={`Ada ${unpaid} pesanan yang belum dibayar. Pesanan ini otomatis kedaluwarsa jika dibiarkan.`}
              to="/admin/orders"
              cta="Lihat transaksi"
            />
          )}

          {/* Tindakan */}
          <section className="mt-10">
            <h2 className="font-heading text-lg font-bold text-ink-primary mb-4">
              Kelola toko
            </h2>
            <div className="grid gap-4 sm:grid-cols-2">
              {ACTIONS.map((a) => (
                <Link
                  key={a.to}
                  to={a.to}
                  className="group border border-border bg-surface rounded-lg p-5
                             hover:border-accent transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-heading text-base font-bold text-ink-primary">
                      {a.title}
                    </h3>
                    <span className="text-ink-dim group-hover:text-accent transition-colors">
                      →
                    </span>
                  </div>
                  <p className="mt-2 font-sans text-sm text-ink-muted leading-relaxed">
                    {a.desc}
                  </p>
                </Link>
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function Metric({
  label,
  value,
  tone = "normal",
}: {
  label: string;
  value: number | string;
  tone?: "normal" | "warn";
}) {
  return (
    <div className="border border-border bg-surface rounded-lg p-4">
      <p className="font-sans text-xs text-ink-muted">{label}</p>
      <p
        className={`mt-1.5 font-mono text-2xl tabular-nums ${
          tone === "warn" ? "text-status-pending" : "text-ink-primary"
        }`}
      >
        {value}
      </p>
    </div>
  );
}

function Notice({ text, to, cta }: { text: string; to: string; cta: string }) {
  return (
    <div className="border border-border-strong bg-elevated rounded-lg px-5 py-4
                    flex flex-col sm:flex-row sm:items-center gap-3 justify-between mb-3">
      <p className="font-sans text-sm text-ink-primary">{text}</p>
      <Link to={to} className="shrink-0">
        <Button variant="secondary">{cta}</Button>
      </Link>
    </div>
  );
}

function NotAuthorized({ needs }: { needs: string }) {
  return (
    <div className="max-w-xl mx-auto px-4 py-20 text-center">
      <MonoText size="sm" color="status-failed">[ AKSES_DITOLAK ]</MonoText>
      <h1 className="mt-4 font-heading text-2xl font-bold text-ink-primary">
        Halaman ini butuh {needs}
      </h1>
      <Link to="/" className="inline-block mt-6">
        <Button variant="secondary">Kembali ke beranda</Button>
      </Link>
    </div>
  );
}
